/**
 * 설계서 — 테이블 하나의 인덱스·파티션 설계 문서와 그 테이블의 **설계 이력**(2026-10-06).
 *
 * 요구: 인덱스 설계 「6) 설계서 작성 7) 테이블 단위 설계 이력 유지」, 파티션 설계 「4) 설계서 작성 5) 테이블 단위 설계 이력 유지」.
 *
 * 설계안을 만들 때마다 서버가 **초안을 이력에 남긴다**(backend tableAnalysis/designDoc.js — LLM 없이 근거·리포트를 틀에
 * 옮겨 적는다). 여기서는 그것을 고쳐 저장하고(고친 판이 남고, AI 가 낸 리포트 원본은 서버에 그대로 있다), 내려받고,
 * 같은 테이블의 지난 설계를 열어 본다. 이력은 **자기 것만** 보인다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FiSave, FiDownload, FiTrash2, FiLoader, FiEdit3, FiEye, FiClock } from 'react-icons/fi';
import { listTableDesigns, getTableDesign, saveTableDesign, deleteTableDesign } from '../../services/assistantClient';
import { saveTextFile } from '../../utils/desktopFile';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';

/** 이력 목록의 시각 — 서버는 UTC(SQLite datetime('now')) 로 준다 */
const localTime = (s) => {
    if (!s) return '';
    const d = new Date(`${String(s).replace(' ', 'T')}Z`);
    return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

function DesignDocPanel({ connectionId, owner, table, latestId }) {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [history, setHistory] = useState([]);
    const [selectedId, setSelectedId] = useState(latestId || '');
    const [design, setDesign] = useState(null);     // 연 설계 { id, kind, title, doc, edited, createdAt }
    const [draft, setDraft] = useState('');
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);

    const loadHistory = useCallback(async () => {
        try {
            const r = await listTableDesigns({ connectionId, owner, table });
            setHistory(r.designs || []);
            return r.designs || [];
        } catch (err) {
            toast.error(formatApiError(err, t));
            return [];
        }
    }, [connectionId, owner, table, t]);

    // 새 설계가 나오면 그것을 연다. 없으면(이 테이블을 처음 여는 경우) 가장 최근 이력
    useEffect(() => {
        let alive = true;
        loadHistory().then((list) => {
            if (!alive) return;
            setSelectedId(latestId || (list[0] && list[0].id) || '');
        });
        return () => { alive = false; };
    }, [loadHistory, latestId]);

    useEffect(() => {
        if (!selectedId) { setDesign(null); setDraft(''); return undefined; }
        let alive = true;
        getTableDesign(selectedId)
            .then((r) => { if (alive) { setDesign(r.design); setDraft(r.design.doc || ''); setEditing(false); } })
            .catch((err) => { if (alive) toast.error(formatApiError(err, t)); });
        return () => { alive = false; };
    }, [selectedId, t]);

    const dirty = !!design && draft !== design.doc;

    const save = useCallback(async () => {
        if (!design) return;
        setBusy(true);
        try {
            const r = await saveTableDesign(design.id, { doc: draft });
            setDesign(r.design);
            setEditing(false);
            await loadHistory();
            toast.success(t('assistant:design.saved'));
        } catch (err) {
            toast.error(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [design, draft, loadHistory, t]);

    const download = useCallback(async () => {
        if (!design) return;
        const stamp = storedTime(design.createdAt, 'date').replace(/-/g, '');
        const name = `${owner}.${table}_${design.kind}-design_${stamp}.md`;
        const r = await saveTextFile({ fileName: name, content: draft, kind: 'text' });
        if (r && !r.ok && !r.canceled) toast.error(r.detail || r.reason || t('assistant:design.downloadFailed'));
    }, [design, draft, owner, table, t]);

    const remove = useCallback(async (item) => {
        const ok = await confirm({
            title: t('assistant:design.deleteTitle'),
            message: t('assistant:design.deleteConfirm', { title: item.title, at: localTime(item.createdAt) }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        setBusy(true);
        try {
            await deleteTableDesign(item.id);
            const list = await loadHistory();
            if (item.id === selectedId) setSelectedId((list[0] && list[0].id) || '');
        } catch (err) {
            toast.error(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [confirm, loadHistory, selectedId, t]);

    /** 고치던 것이 있으면 다른 이력으로 넘어가기 전에 묻는다 — 말없이 버리지 않는다 */
    const open = useCallback(async (id) => {
        if (id === selectedId) return;
        if (dirty) {
            const ok = await confirm({
                title: t('assistant:design.discardTitle'),
                message: t('assistant:design.discardConfirm'),
                confirmText: t('assistant:design.discard'),
                variant: 'danger',
            });
            if (!ok) return;
        }
        setSelectedId(id);
    }, [selectedId, dirty, confirm, t]);

    return (
        <div className="ta-design">
            <div className="tcf-checkHead">{t('assistant:design.docTitle')}</div>
            {!design ? (
                <div className="tcf-hint">{t('assistant:design.noDoc')}</div>
            ) : (
                <>
                    <div className="tcf-row">
                        <span className="tcf-hint">
                            {design.title} · {localTime(design.createdAt)}
                            {design.edited ? ` · ${t('assistant:design.edited')}` : ''}
                        </span>
                        <span className="ta-designActions">
                            <button type="button" className="tcf-btn" onClick={() => setEditing(v => !v)}>
                                {editing ? <FiEye size={11} /> : <FiEdit3 size={11} />}
                                {t(editing ? 'assistant:design.preview' : 'assistant:design.edit')}
                            </button>
                            <button type="button" className="tcf-btn tcf-primary" disabled={!dirty || busy} onClick={save}>
                                {busy ? <FiLoader size={11} className="tcf-spin" /> : <FiSave size={11} />}
                                {t('common:action.save')}
                            </button>
                            <button type="button" className="tcf-btn" onClick={download} title={t('assistant:design.downloadTip')}>
                                <FiDownload size={11} /> {t('assistant:design.download')}
                            </button>
                        </span>
                    </div>
                    {editing ? (
                        <textarea
                            className="tcf-input ta-designEditor" rows={22} value={draft}
                            onChange={(e) => setDraft(e.target.value)} spellCheck={false}
                        />
                    ) : (
                        <div className="ta-designView">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft}</ReactMarkdown>
                        </div>
                    )}
                </>
            )}

            {/* 이 테이블의 설계 이력 — 자기 것만, 최근 순 */}
            <div className="tcf-checkHead"><FiClock size={11} /> {t('assistant:design.history', { count: history.length })}</div>
            {history.length ? (
                <ul className="ta-designHistory">
                    {history.map(h => (
                        <li key={h.id} className={h.id === selectedId ? 'ta-designOn' : ''}>
                            <button type="button" className="ta-designPick" onClick={() => open(h.id)}>
                                <span className="ta-meta">{t(`assistant:design.kind.${h.kind}`)}</span>
                                {localTime(h.createdAt)}
                                {h.edited ? <span className="ta-meta">{t('assistant:design.edited')}</span> : null}
                            </button>
                            <button
                                type="button" className="tcf-iconBtn" disabled={busy}
                                title={t('common:ui.delete')} onClick={() => remove(h)}
                            >
                                <FiTrash2 size={11} />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : <div className="tcf-hint">{t('assistant:design.noHistory')}</div>}
        </div>
    );
}

DesignDocPanel.propTypes = {
    connectionId: PropTypes.string.isRequired,
    owner: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
    latestId: PropTypes.string,
};
DesignDocPanel.defaultProps = { latestId: '' };

export default DesignDocPanel;
